import React, { forwardRef, useEffect, useRef } from 'react';
import clsx from 'clsx';
import DOMPurify from 'isomorphic-dompurify';

import {
  MLogoContainerProps,
  MLogoContainerBrand,
  MLogoContainerSize,
  MLogoContainerAsLinkProps,
} from './MLogoContainer.types';
import { StyledLogoContainer } from './MLogoContainer.styles';
import { getLogoName } from '../../utils/helper';
import { AImage } from '../../atoms/AImage';

const fetchSvg = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch SVG: ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    console.error(error);
    return null;
  }
};

const COMPONENT_NAME = 'm-web-ui-kit-MLogoContainer';
const TEST_ID = 'web-ui-kit-MLogoContainer';

export const MLogoContainer = forwardRef<HTMLSpanElement | HTMLAnchorElement | null, MLogoContainerProps>(
  (props, ref) => {
    const {
      brand,
      aspectRatio,
      width,
      height,
      color,
      alignment = 'Centered',
      isLink,
      linkUrl,
      hoverColor,
      backgroundColor,
      ariaRole,
      logopath,
      aspectMode,
      brandFullName,
      dacLogoUrl,
      isDACLogo = false,
      isLazy = true,
      renditions,
      fetchpriority,
      dynamic,
      customClass = '',
      ...rest
    } = props;
    const svgContainerRef = useRef<HTMLSpanElement>(null);
    const logoName = brand ? getLogoName(brand) : 'NA';
    const brandName = brandFullName?.trim() || logoName;
    const nextData =
      (typeof window !== 'undefined' &&
        window.document.getElementById('__NEXT_DATA__') &&
        document.getElementById('__NEXT_DATA__')?.innerText) ||
      '';
    let isAuthorMode = false;
    if (nextData) {
      isAuthorMode = JSON.parse(nextData).props.pageProps.isAuthorMode;
    }

    const domainURL = isAuthorMode ? 'https://www.dev-cdn.marriott.com' : process.env['NEXT_PUBLIC_ASSET_DOMAIN'];
    const resolvedDomain = domainURL || (typeof window !== 'undefined' ? window.location.origin : '');
    const logoUrl =
      logopath ||
      (logoName && logoName !== 'NA' && resolvedDomain
        ? `${resolvedDomain}/mi-assets/mi-global/brand-logos/${logoName}.svg`
        : '');

    // Simple logic: if isDACLogo is true and dacLogoUrl is provided, render DAC image
    const shouldRenderDacImage = isDACLogo && dacLogoUrl;

    useEffect(() => {
      if (isDACLogo || !logoUrl) {
        // For DAC logos (regardless of dacLogoUrl) or missing SVG URLs, clear SVG container
        svgContainerRef.current?.replaceChildren();
        return;
      }
      const fetchSvgContent = async () => {
        const content = await fetchSvg(logoUrl);
        updateSvgContent(content || '');
      };

      fetchSvgContent();
    }, [logoUrl, isDACLogo]);

    const updateSvgContent = (content: string) => {
      if (content) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'image/svg+xml');
        const svgElement = doc.querySelector('svg');

        if (svgElement !== null) {
          try {
            const [scaleX, scaleY] = aspectRatio.split(':');

            const wsf = Number(svgElement.getAttribute(`wsf-${scaleX}x${scaleY}`) || '1').toFixed(2);
            const hsf = Number(svgElement.getAttribute(`hsf-${scaleX}x${scaleY}`) || '1').toFixed(2);

            if (typeof width === 'number' && typeof height === 'number') {
              const scaledWidth = width * Number(wsf);
              const scaledHeight = height * Number(hsf);

              svgElement.style.width = `${scaledWidth}px`;
              svgElement.style.height = `${scaledHeight}px`;
            } else {
              svgElement.style.width = `${width}`;
              svgElement.style.height = `${height}`;
            }

            // Use setAttribute instead of direct assignment as preserveAspectRatio is readonly
            svgElement.setAttribute('preserveAspectRatio', aspectMode || '');
            svgElement.setAttribute('role', 'img');
            svgElement.setAttribute('aria-label', brandName);
            const stringifiedSVG: string = new XMLSerializer().serializeToString(svgElement);
            const sanitizedNode = DOMPurify.sanitize(stringifiedSVG, {
              RETURN_DOM_FRAGMENT: true,
            }) as DocumentFragment;
            svgContainerRef.current?.replaceChildren(sanitizedNode);
          } catch (error) {
            console.error('Error processing SVG content:', error);
            svgContainerRef.current?.replaceChildren();
          }
        } else {
          svgContainerRef.current?.replaceChildren();
          console.log('No SVG elements found.');
        }
      } else {
        svgContainerRef.current?.replaceChildren();
        console.log('No SVG elements found.');
      }
    };

    return (
      <StyledLogoContainer
        data-testid={TEST_ID}
        className={clsx(backgroundColor)}
        data-component-name={COMPONENT_NAME}
        as={isLink ? 'a' : 'span'}
        href={linkUrl || undefined}
        data-logoid={brand}
        containerWidth={width}
        containerHeight={height}
        isLink={isLink || false}
        hoverColor={hoverColor || ''}
        color={color || ''}
        ref={ref}
        {...rest}
      >
        {shouldRenderDacImage ? (
          <AImage
            src={dacLogoUrl!}
            alt={brandName}
            title={brandName}
            imgClass={clsx(
              { 'bottom-left': alignment === 'Bottom Left' },
              { 'center-left': alignment === 'Center Left' },
              { 'bottom-center': alignment === 'Bottom Centered' },
              { centered: alignment === 'Centered' },
              { 'branded-logo': color === 'branded' },
              { 'color-logo': color === 'color' },
              { 'grey-logo': color === 'grey' },
              { 'white-logo': color === 'white' },
              { 'background-aware-logo': color === 'background-aware' },
              { 'alternate-logo': color === 'alternate-logo' },
              customClass
            )}
            loading={isLazy ? 'lazy' : 'eager'}
            renditions={renditions}
            fetchpriority={fetchpriority}
            dynamic={dynamic}
            style={{
              width: `${typeof width === 'number' ? `${width}px` : width}`,
              height: `${typeof height === 'number' ? `${height}px` : height}`,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        ) : (
          <span
            ref={svgContainerRef}
            role={ariaRole}
            className={clsx(
              { 'bottom-left': alignment === 'Bottom Left' },
              { 'center-left': alignment === 'Center Left' },
              { 'bottom-center': alignment === 'Bottom Centered' },
              { centered: alignment === 'Centered' },
              { 'branded-logo': color === 'branded' },
              { 'color-logo': color === 'color' },
              { 'grey-logo': color === 'grey' },
              { 'white-logo': color === 'white' },
              { 'background-aware-logo': color === 'background-aware' },
              { 'alternate-logo': color === 'alternate-logo' }
            )}
          ></span>
        )}
        {isLink && (rest as MLogoContainerAsLinkProps)?.target === '_blank' && (
          <span className="sr-only">(opens in new window)</span>
        )}
      </StyledLogoContainer>
    );
  }
);

export { MLogoContainerBrand, MLogoContainerSize };
